import { FC, Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import Typography from 'app/components/Typography';

import { useBoolean } from 'app/hooks';
import { usePolicyInUser } from 'app/hooks/policy';

import { REACT_APP_VERSION } from 'app/constants/environments';
import { PATH } from 'app/constants/path';

import { ReactComponent as MailIcon } from 'app/assets/icons/email-icon.svg';
import { ReactComponent as CompanyInfoIcon } from 'app/assets/icons/hospital.svg';
import { ReactComponent as ViewPermissionIcon } from 'app/assets/icons/key.svg';
import { ReactComponent as UserIcon } from 'app/assets/icons/profile-2user.svg';
import { ReactComponent as SettingIcon } from 'app/assets/icons/setting-2.svg';
import { ReactComponent as UserRoleIcon } from 'app/assets/icons/wrench-icon.svg';

import { useAuth } from '../../../../app/modules/auth';
import { toAbsoluteUrl } from '../../../helpers';
import { GroupMenu, GroupTitle, MenuSub } from 'theme/layout/components/aside/AsideDefault';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const ShowAvatar = styled.div`
  width: 36px;
  height: 36px;
  margin-right: 10px;
`;

const Text = styled.a`
  border-left: none !important;
  :hover {
    background-color: var(--primary-2) !important;
    p {
      color: var(--primary);
    }
  }
`;

const MenuContainer = styled.div`
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
  width: 275px;
`;

type MenuItem = {
  title: React.ReactNode;
  path: string;
  icon?: string;
  className?: string;
  pathActive?: string[];
  menuIcon?: React.ElementType;
};

const HeaderUserMenu: FC<{
  setVisible: React.Dispatch<React.SetStateAction<boolean>> | undefined;
}> = ({ setVisible }) => {
  const { currentUser, logout } = useAuth();
  const permission = usePolicyInUser(currentUser?.policy ?? []);
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const isFirstMenu = useBoolean(false);
  const navigate = useNavigate();
  const orgId = useOrganizationStore((state) => state.state.organization?.id);
  const params = useParams<{ orgId: string }>();
  const organizationId = params.orgId ? Number(params.orgId) : orgId;

  useEffect(() => {
    if (!permission || isFirstMenu.value) {
      return;
    }
    const newMenu: MenuItem[] = [];

    if (permission.systemAdmin) {
      newMenu.push({
        title: 'Organizations',
        menuIcon: CompanyInfoIcon,
        path: PATH.ORGANIZATIONS,
        pathActive: [PATH.ORGANIZATIONS],
      });
    }

    if (permission.systemAdmin) {
      newMenu.push({
        title: 'User roles',
        menuIcon: UserRoleIcon,
        path: PATH.USERS_ROLE_WEB,
        pathActive: [PATH.USERS_ROLE_WEB, PATH.USERS_ROLE_TABLET],
      });
      newMenu.push({
        title: 'View permission',
        menuIcon: ViewPermissionIcon,
        path: PATH.PERMISSION,
      });
    }

    if (permission.systemAdmin) {
      newMenu.push({
        title: 'Mail tracking',
        menuIcon: MailIcon,
        path: PATH.MAIL_HISTORY,
        pathActive: [PATH.MAIL_HISTORY, PATH.MAIL_HISTORY_CONFIRM],
      });
    }

    if (permission.systemAdmin) {
      newMenu.push({
        title: 'User management',
        menuIcon: UserIcon,
        path: PATH.USER_MANAGEMENT_SYSTEM
          ? PATH.USER_MANAGEMENT_SYSTEM_WEB
          : PATH.USER_MANAGEMENT_SYSTEM_TABLET,
        pathActive: [PATH.USER_MANAGEMENT_SYSTEM_WEB, PATH.USER_MANAGEMENT_SYSTEM_TABLET],
      });
    }

    if (permission.systemAdmin) {
      newMenu.push({
        title: 'Configuration settings',
        menuIcon: SettingIcon,
        path: PATH.CONFIGURATIONS,
        pathActive: [
          PATH.AUTO_LOGOUT_TIME,
          PATH.ITEM_UNIT,
          PATH.CONFIGURATION_STOCK_RUN,
          PATH.CONFIGURATION_WORK_SHIFT,
          PATH.CONFIGURATION_STOCK_VALUES,
        ],
        className: 'section-system',
      });
    }

    setMenu(newMenu);
    isFirstMenu.setValue(true);
  }, [permission]);

  const handleMenuItemClick = (path: string) => () => {
    navigate(`/org/${organizationId}${path}`);
    setVisible?.(false);
  };

  return (
    <MenuContainer>
      {/* <div
      className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg menu-state-primary fw-bold fs-6 w-275px"
      data-kt-menu="true"
    > */}
      <div className="menu-item" style={{ padding: 24 }}>
        <div className="d-flex align-items-center ">
          <ShowAvatar>
            <img alt="Logo" src={toAbsoluteUrl('/media/avatars/icon-avatar-default.svg')} />
          </ShowAvatar>

          <div className="d-flex flex-column">
            <Typography variant="title-4" color="neutral-10">
              {`${currentUser?.firstName} ${currentUser?.lastName}`}
            </Typography>
            <Typography fontSize={12} fontWeight={500} lineHeight={20} color="neutral-6">
              {currentUser?.role.name}
            </Typography>
          </div>
        </div>
      </div>

      <div className="separator"></div>
      <div className="menu-item" style={{ padding: '12px 16px', maxHeight: 330, overflow: 'auto' }}>
        <div
          className="menu menu-column menu-active-bg menu-hover-bg menu-title-gray-700 fs-6 menu-rounded w-100"
          // id="#kt_aside_menu"
          // data-kt-menu="true"
          style={{ flexGrow: 1, overflow: 'auto' }}
        >
          <Fragment>
            {permission.systemAdmin ? (
              <>
                <GroupTitle style={{ marginTop: 0 }}>
                  <Typography variant="body-8" color="neutral-6" className="text-uppercase">
                    SYSTEM
                  </Typography>
                </GroupTitle>
                {menu.map((item, i) => {
                  const isPathActive =
                    item.pathActive && item.pathActive.length
                      ? Boolean(item.pathActive.find((el) => location.pathname.includes(el)))
                      : location.pathname.includes(item.path);
                  return (
                    <MenuSub active={isPathActive} key={i}>
                      <div
                        onClick={handleMenuItemClick(item.path)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          height: 38,
                          width: '100%',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {/* <PointMenu className="point" /> */}
                          <GroupMenu
                            label={item.title}
                            icon={item.menuIcon as React.ElementType}
                            active={isPathActive}
                          />
                        </div>
                      </div>
                    </MenuSub>
                  );
                })}
              </>
            ) : null}
          </Fragment>
        </div>
        <Text onClick={logout} className="menu-link" style={{ padding: '6px 8px' }}>
          <Typography variant="body-3">Sign Out</Typography>
        </Text>
        {REACT_APP_VERSION && (
          <Typography
            fontSize={12}
            fontWeight={400}
            lineHeight={20}
            color="neutral-7"
            style={{ marginLeft: '8px', marginTop: '12px' }}
          >
            {REACT_APP_VERSION}
          </Typography>
        )}
      </div>
      {/* </div> */}
    </MenuContainer>
  );
};

export { HeaderUserMenu };
